import React, { useState, useEffect, useRef } from "react";
import { Button, Switch, CircularProgress } from "@nextui-org/react";
import { LuMail } from "react-icons/lu";
import { useTranslation } from "react-i18next";

import myAxios from "../../utils/axiosWrapper";
import { auth } from "../../conf/firebase";

const describeError = (error) =>
  [error?.response?.status, error?.response?.data?.detail || error?.message]
    .filter(Boolean)
    .join(" - ");

const DailyDigestPanel = ({
  apiUrl,
  user,
  organisations,
  company_config,
  onDirtyChange = () => {},
}) => {
  const { t } = useTranslation();

  // Departments for which the daily digest is turned OFF (empty = all enabled).
  const [disabledIds, setDisabledIds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [previewHtml, setPreviewHtml] = useState("");
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const initialSnapshotRef = useRef("[]");

  const departments = organisations?.departments || [];
  const isFeatureEnabled = !!company_config?.feature_flags?.send_daily_emails_digest;

  const normalize = (ids) => [...ids].sort();
  const isDirty = JSON.stringify(normalize(disabledIds)) !== initialSnapshotRef.current;

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty]);

  const fetchSettings = async () => {
    setIsLoading(true);
    setErrorMessage("");
    try {
      const response = await myAxios.get(
        `${apiUrl}/audio/api/v1/admin/get_company_daily_digest_settings`
      );
      const ids = response?.data?.[0]?.departments_ids_without_daily_digest || [];
      setDisabledIds(ids);
      initialSnapshotRef.current = JSON.stringify(normalize(ids));
    } catch (error) {
      console.error("Error fetching daily digest settings:", error);
      setErrorMessage(
        `${t("Impossible de charger les reglages du recapitulatif quotidien.")} (${describeError(
          error
        )})`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPreview = async () => {
    setIsPreviewLoading(true);
    try {
      const response = await myAxios.get(
        `${apiUrl}/audio/api/v1/admin/company_daily_digest_preview`
      );
      setPreviewHtml(response?.data?.[0]?.html || "");
    } catch (error) {
      console.error("Error fetching daily digest preview:", error);
    } finally {
      setIsPreviewLoading(false);
    }
  };

  // On a page refresh the Firebase user is restored asynchronously: a request
  // sent before that goes out without a token and gets a 401. Wait for it.
  useEffect(() => {
    if (!auth.currentUser) {
      return;
    }
    fetchSettings();
    fetchPreview();
  }, [user]);

  const handleToggle = (departmentId, isEnabled) => {
    setDisabledIds((previous) =>
      isEnabled ? previous.filter((id) => id !== departmentId) : [...previous, departmentId]
    );
  };

  const handleCancel = () => {
    setDisabledIds(JSON.parse(initialSnapshotRef.current));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setErrorMessage("");
    try {
      await myAxios.put(`${apiUrl}/audio/api/v1/admin/update_company_daily_digest_settings`, {
        departments_ids_without_daily_digest: disabledIds,
      });
      await fetchSettings();
    } catch (error) {
      console.error("Error saving daily digest settings:", error);
      setErrorMessage(
        `${t("Impossible d'enregistrer les modifications.")} (${describeError(error)})`
      );
    } finally {
      setIsSaving(false);
    }
  };

  const enabledCount = departments.filter((d) => !disabledIds.includes(d.id)).length;

  return (
    <div className="bg-white rounded-2xl p-6 max-w-[800px]">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <LuMail
            size={20}
            className="text-primary"
          />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-[#171717]">{t("Récapitulatif quotidien")}</h2>
          <p className="text-xs text-[#737373]">
            {enabledCount} / {departments.length} {t("departements actifs")}
          </p>
        </div>
      </div>

      <p className="text-sm text-gray-500 mt-4">
        {t(
          "Choisissez les departements dont les operateurs recoivent le recapitulatif quotidien de leurs appels par email."
        )}
      </p>

      {!isFeatureEnabled && (
        <p className="text-sm text-amber-600 bg-amber-50 rounded-lg px-3 py-2 mt-4">
          {t(
            "Le recapitulatif quotidien est desactive pour cette entreprise : aucun email ne sera envoye, quel que soit le reglage ci-dessous."
          )}
        </p>
      )}

      {errorMessage && (
        <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 mt-4">{errorMessage}</p>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <CircularProgress />
        </div>
      ) : departments.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <span className="text-sm">{t("Aucun departement")}</span>
        </div>
      ) : (
        <div className="mt-4 divide-y divide-[#E5E5E5] border border-[#E5E5E5] rounded-xl">
          {departments.map((department) => (
            <div
              key={department.id}
              className="flex items-center justify-between px-4 py-3"
            >
              <span className="text-sm text-[#171717]">{department.name}</span>
              <Switch
                size="sm"
                aria-label={department.name}
                isSelected={!disabledIds.includes(department.id)}
                onValueChange={(isEnabled) => handleToggle(department.id, isEnabled)}
              />
            </div>
          ))}
        </div>
      )}

      <div className="mt-8">
        <h3 className="text-sm font-semibold text-[#171717]">{t("Apercu de l'email")}</h3>
        <p className="text-xs text-[#737373] mt-1">
          {t(
            "Exemple genere avec des appels fictifs, le logo et les libelles de votre entreprise."
          )}
        </p>
        {isPreviewLoading ? (
          <div className="flex items-center justify-center py-12">
            <CircularProgress />
          </div>
        ) : (
          previewHtml && (
            <iframe
              title={t("Apercu de l'email")}
              srcDoc={previewHtml}
              sandbox=""
              className="w-full h-[720px] mt-3 border border-[#E5E5E5] rounded-xl bg-[#f7f8fc]"
            />
          )
        )}
      </div>

      {isDirty && (
        <div className="sticky bottom-4 mt-6 flex items-center justify-between gap-3 bg-[#171717] text-white rounded-xl px-4 py-3 shadow-lg">
          <span className="text-sm">{t("Unsaved changes")}</span>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="flat"
              className="bg-white/10 text-white"
              onClick={handleCancel}
              isDisabled={isSaving}
            >
              {t("Annuler")}
            </Button>
            <Button
              size="sm"
              color="primary"
              onClick={handleSave}
              isLoading={isSaving}
            >
              {t("Sauvegarder les modifications")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyDigestPanel;
